import { Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import React from 'react';
import { v4 as uuid } from 'uuid';
import {
  Address,
  ApplicationAction,
  ApplicationActionType,
  Modal,
  Recipient,
  isUserUnderAgeIdNumber,
  stripWhitespace,
  validateAddressField,
} from 'src/features/common';
import { CartItem } from 'src/features/common/types/application/cart';
import { AddressStep } from 'src/features/dpo/components/delivery-address-modal-v2/address-step/AddressStep';
import { AllocateDevicesStep } from 'src/features/dpo/components/delivery-address-modal-v2/allocate-devices-step/AllocateDevicesStep';
import { ReceiverDetailsStep } from 'src/features/dpo/components/delivery-address-modal-v2/receiver-details-step/ReceiverDetailsStep';
import {
  hasNoSpecialCharacters,
  isValidID,
  isValidMobileNumber,
  isValidName,
} from 'src/helpers/validation';
import { updateDeviceDeliveryAddress } from 'src/features/order/helpers/delivery-helpers';
import { useApplication } from 'src/hooks/useApplication';
import { Button } from '@vfs-digital-channels/ns-react-components';
import { notify } from 'src/features/common/components/toast/toast';
import { getProductNamesInCart } from '../../helpers/tealium-service-helpers';
import { PriceType } from '@vfs-digital-channels/ns-utils';

export type Page = React.ReactFragment;

export type AddressModalPageProps = Delivery & {
  currentQuantity?: number;
  validateForm: (values?: Delivery) => Promise<FormikErrors<Delivery>>;
  disableDeviceInput: boolean;
};

export type AddressModalWizardPageStep = {
  page: (props: AddressModalPageProps) => Page;
  name: string;
};

export type DeliveryAddressProps = {
  show: boolean;
  onClose: () => void;
  valuesToEdit?: Address;
  device?: CartItem;
  deliveryAddress?: { id: string; address: string };
  currentDeliveryId?: string;
  currentQuantity?: number;
  jumpToStep?: number;
  changeAddress?: boolean;
  currentAddressId?: string;
};

export type Delivery = {
  address: Address;
  receiverDetails: Recipient;
  device: CartItem;
  deliveryAddress: { id: string; address: string };
  quantity: number;
};

export function DeliveryAddressModalV2(props: DeliveryAddressProps) {
  const [currentStepIndex, setCurrentStepIndex] = React.useState<number>(0);
  const { application, dispatch, dpoTealiumService } = useApplication();
  const steps: AddressModalWizardPageStep[] = [
    {
      page: () => <AddressStep />,
      name: 'Address',
    },
    {
      page: (props: AddressModalPageProps) => <ReceiverDetailsStep {...props} />,
      name: 'Details',
    },
    {
      page: (props: AddressModalPageProps) => <AllocateDevicesStep {...props} />,
      name: 'Allocate',
    },
  ];

  const productNames = getProductNamesInCart(application.cart || []);

  React.useEffect(() => {
    if (props.show) {
      setCurrentStepIndex(props.jumpToStep || 0);
    }
  }, [props.show]);

  React.useEffect(() => {
    if (props.show) {
      switch (currentStepIndex) {
        case 0:
          dpoTealiumService?.onAddDeliveryAddressModalView(productNames);
          break;
        case 1:
          dpoTealiumService?.onReceiverDetailsModalView(productNames);
          break;
        case 2:
          dpoTealiumService?.onAssignDeliveryAddressModalView(productNames);
          break;
      }
    }
  }, [currentStepIndex, props.show]);

  const getInitialValues = (): Delivery => {
    if (props.jumpToStep) {
      return {
        device: props.device,
        deliveryAddress: props.deliveryAddress,
        quantity: props.currentQuantity || 1,
      } as Delivery;
    }

    if (props.valuesToEdit) {
      const { recipient, ...address } = props.valuesToEdit;
      return {
        address: address,
        receiverDetails: recipient,
      } as Delivery;
    }

    return {
      address: {
        street: '',
        building: '',
        suburb: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
      },
      receiverDetails: {
        tradingAs: '',
        managerName: '',
        idNumber: '',
        phone: '',
        workPhone: '',
        cellPhone: '',
      },
      device: {},
      deliveryAddress: {},
      quantity: 1,
    } as Delivery;
  };

  function validateStepOne(values: Delivery) {
    const errors: FormikErrors<Delivery> = {};

    const addressErrors = validateAddressField(values.address);
    if (addressErrors) {
      errors.address = addressErrors;
    }
    return errors;
  }

  const validateRecipient = (values: Recipient) => {
    const errors: FormikErrors<Recipient> = {};

    if (!isValidName(values.managerName) || !values?.managerName) {
      errors.managerName = 'The recipient full names entered are invalid';
    }

    if (!values?.idNumber || !isValidID(stripWhitespace(values.idNumber))) {
      errors.idNumber = 'The recipient ID number entered is invalid';
    }

    if (
      values.idNumber &&
      isValidID(stripWhitespace(values.idNumber)) &&
      isUserUnderAgeIdNumber(values.idNumber)
    ) {
      errors.idNumber = 'The age limit is 18 years or older';
    }

    if (!isValidMobileNumber(stripWhitespace(values.cellPhone)) || !values?.cellPhone) {
      errors.cellPhone = 'The recipient cell number entered is invalid';
    }

    if (!isValidMobileNumber(stripWhitespace(values.workPhone)) || !values?.workPhone) {
      errors.workPhone = 'The recipient work number entered is invalid';
    }

    if (!hasNoSpecialCharacters(values.tradingAs) || !values?.tradingAs) {
      errors.tradingAs = 'The trading name entered is invalid';
    }

    return errors;
  };

  function validateStepTwo(values: Delivery) {
    const errors: FormikErrors<Delivery> = {};

    const recipientErrors = validateRecipient(values.receiverDetails);
    if (Object.values(recipientErrors).length > 0) {
      errors.receiverDetails = recipientErrors;
    }
    return errors;
  }

  function validateStepThree(values: Delivery) {
    const errors: { [x: string]: string } = {};

    if (!values.deliveryAddress?.address) {
      errors.deliveryAddressId = 'Please select new delivery address';
    }

    if (!values.device?.productId) {
      errors.deviceId = 'At least one solution needs to be added';
    }
    return errors;
  }

  function validate(values: Delivery, stepIndex: number) {
    let errors: FormikErrors<Delivery> = {};

    if (props.jumpToStep === 2) return validateStepThree(values);

    switch (stepIndex) {
      case 0:
        errors = validateStepOne(values);
        break;
      case 1:
        errors = validateStepTwo(values);
        break;
      case 2:
        errors = validateStepThree(values);
        break;
      default:
        return {};
    }

    return errors;
  }

  const addressExists = (addresses: Address[], addressId: string): boolean => {
    return addresses.some((address) => address.id === addressId);
  };

  const submit = (values: Delivery, helpers: FormikHelpers<Delivery>) => {
    if (currentStepIndex === 1 && props.valuesToEdit) {
      dispatch({
        type: ApplicationActionType.UPDATE_ADDRESS,
        address: values.address,
        recipient: values.receiverDetails,
      });
      helpers.resetForm();
      setCurrentStepIndex(0);
      props.onClose();
    }

    if (currentStepIndex === 1 && !props.valuesToEdit) {
      notify({
        children: 'Address added successfully',
        autoClose: 5000,
      });
    }

    if (currentStepIndex + 1 < steps.length) {
      if (!values.address.id) {
        helpers.setFieldValue('address.id', uuid());
      }
      setCurrentStepIndex(currentStepIndex + 1);
      return;
    }

    const { deliveries, collections } = updateDeviceDeliveryAddress(
      application.deliveries || [],
      application.collections || [],
      values.device.productId,
      values.deliveryAddress.id,
      values.quantity,
      values.device.id,
      props.currentAddressId || '',
      values.device.priceType as PriceType,
      props.currentDeliveryId,
    );

    const actions: ApplicationAction[] = [
      { type: ApplicationActionType.UPDATE_DEVICE_DELIVERIES, deliveries },
    ];

    if (!addressExists(application.addresses || [], values.deliveryAddress.id)) {
      actions.push({
        type: ApplicationActionType.ADD_ADDRESS,
        address: values.address,
        recipient: values.receiverDetails,
      });
    }

    if (collections) {
      actions.push({ type: ApplicationActionType.UPDATE_DEVICE_COLLECTIONS, collections });
    }

    dispatch({ type: ApplicationActionType.MULTIPLE_ACTIONS, actions });
    helpers.resetForm();
    setCurrentStepIndex(0);
    props.onClose();
  };

  const currentStep = steps[currentStepIndex];
  const formRef = React.useRef<FormikProps<Delivery>>(null);

  return (
    <Modal
      onClose={() => {
        switch (currentStepIndex) {
          case 0:
            dpoTealiumService?.closeAddDeliveryAddressModal(
              getInitialValues().address,
              formRef.current?.values.address!,
            );
            break;
          case 1:
            dpoTealiumService?.closeReceiverDetailsModal(
              getInitialValues().receiverDetails,
              formRef.current?.values.receiverDetails!,
              'close',
            );
            break;
        }
        props.onClose();
      }}
      visible={props.show}
      allowTabs
    >
      <Formik
        initialValues={getInitialValues()}
        innerRef={formRef}
        validate={(values) => validate(values, currentStepIndex)}
        validateOnBlur
        validateOnChange
        validateOnMount
        enableReinitialize
        onSubmit={submit}
      >
        {({ isValid, values, submitForm, validateForm, setFieldTouched }) => (
          <article className="flex flex-col w-full h-full">
            <section className="flex-[1] flex overflow-hidden justify-center">
              {currentStep.page({
                ...values,
                currentQuantity: props.currentQuantity,
                validateForm,
                disableDeviceInput: !!props.changeAddress,
              })}
            </section>
            <section className="pt-[20px] w-full self-end flex justify-center">
              {currentStepIndex > 0 && !props.changeAddress && (
                <Button
                  onClick={() => {
                    switch (currentStepIndex) {
                      case 1:
                        dpoTealiumService?.closeReceiverDetailsModal(
                          getInitialValues().receiverDetails,
                          values.receiverDetails,
                          'back',
                        );
                        break;
                    }
                    setCurrentStepIndex(currentStepIndex - 1);
                  }}
                  className="w-56 mr-4"
                  variant="outline"
                >
                  Back
                </Button>
              )}
              <Button
                disabled={!isValid}
                onClick={() => {
                  switch (currentStepIndex) {
                    case 0:
                      dpoTealiumService?.onAddDeliveryAddressNext(productNames);
                      break;
                    case 1:
                      dpoTealiumService?.onReceiverDetailsModalNext(productNames);
                      break;
                    case 2:
                      dpoTealiumService?.onAssignAddressModalNext(productNames);
                      break;
                  }

                  submitForm();
                  setFieldTouched('receiverDetails', false);
                }}
                type="button"
                className="w-56 bg-red-600"
              >
                {currentStepIndex === 1 && props.valuesToEdit ? 'Update' : 'Next'}
              </Button>
            </section>
          </article>
        )}
      </Formik>
    </Modal>
  );
}
